import { useState, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Briefcase, MapPin, DollarSign, Phone, Mail, Calendar, Clock, Building2 } from 'lucide-react';
import { getUser } from '@/utils/auth';
import api, { formatSalary } from '@/utils/api';

const AppliedJobs = () => {
  const user = getUser();
  const [applications, setApplications] = useState([]);

  useEffect(() => {
    const fetchApplications = async () => {
      try {
        const response = await api.get(`/job-seeker/applications/${user.id}`);
        // Sort by applied_at descending (latest first)
        const sortedApplications = response.data.sort((a, b) => 
          new Date(b.applied_at) - new Date(a.applied_at)
        );
        setApplications(sortedApplications);
      } catch (error) {
        console.error('Error fetching applications:', error);
      }
    };
    
    if (user?.id) {
      fetchApplications();
    }
  }, [user?.id]);

  const getStatusBadge = (job) => {
    if (!job) return { label: 'Unknown', className: 'bg-slate-100 text-slate-700' };
    
    // Check if job is still active based on last_date and status
    const now = new Date();
    const lastDate = new Date(job.last_date);
    
    if (job.status === 'approved' && job.active && lastDate > now) {
      return { label: 'Active', className: 'bg-green-100 text-green-700' };
    } else if (lastDate < now) {
      return { label: 'Expired', className: 'bg-red-100 text-red-700' };
    } else if (!job.active || job.status !== 'approved') {
      return { label: 'Inactive', className: 'bg-slate-100 text-slate-700' };
    }
    return { label: 'Active', className: 'bg-green-100 text-green-700' };
  };

  const formatDateTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-IN', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };

  return (
    <div>
      <h1 className="text-3xl font-bold text-slate-900 mb-6" style={{fontFamily: 'Outfit'}}>Applied Jobs</h1>
      
      {applications.length === 0 ? (
        <Card className="p-12 text-center border-slate-200">
          <Briefcase className="w-16 h-16 mx-auto text-slate-300 mb-4" />
          <h3 className="text-xl font-semibold text-slate-900 mb-2">No applications yet</h3>
          <p className="text-slate-600">Start applying to jobs to see them here</p>
        </Card>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {applications.map((app) => {
            const statusBadge = getStatusBadge(app.job);
            return (
              <Card key={app.id} className="p-6 border-slate-200 hover:shadow-lg transition-shadow">
                <div className="flex items-start gap-3 mb-4">
                  {app.job?.company_logo ? (
                    <img src={app.job.company_logo} alt="Company" className="w-12 h-12 rounded-lg object-cover border border-slate-200" />
                  ) : (
                    <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                      <Building2 className="w-6 h-6 text-blue-600" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <h3 className="text-lg font-semibold text-slate-900 truncate">{app.job_title}</h3>
                    <p className="text-sm text-slate-600 truncate">{app.company_name}</p>
                  </div>
                </div>

                <div className="space-y-3 mb-4">
                  <div className="flex items-center gap-2 text-sm text-slate-600">
                    <Calendar className="w-4 h-4" />
                    <div>
                      <p className="font-medium text-slate-700">Applied On</p>
                      <p>{formatDateTime(app.applied_at)}</p>
                    </div>
                  </div>

                  {app.job && (
                    <>
                      <div className="flex items-center gap-2 text-sm text-slate-600">
                        <Phone className="w-4 h-4" />
                        <span className="truncate">{app.job.contact_number || 'N/A'}</span>
                      </div>
                      <div className="flex items-center gap-2 text-sm text-slate-600">
                        <Mail className="w-4 h-4" />
                        <span className="truncate">{app.job.email || 'N/A'}</span>
                      </div>
                      <div className="flex items-center gap-2 text-sm text-slate-600">
                        <MapPin className="w-4 h-4" />
                        <span className="truncate">{app.job.locations?.[0]?.city || 'Multiple'}</span>
                      </div>
                      <div className="flex items-center gap-2 text-sm text-slate-600">
                        <DollarSign className="w-4 h-4" />
                        <span>{formatSalary(app.job)}</span>
                      </div>
                    </>
                  )}
                </div>

                <div className="flex items-center justify-between pt-4 border-t border-slate-200">
                  <span className={`inline-block px-3 py-1 rounded-full text-xs font-medium ${statusBadge.className}`}>
                    {statusBadge.label}
                  </span>
                  <span className="inline-block px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
                    {app.status}
                  </span>
                </div>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default AppliedJobs;